import { Component, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Select } from 'primeng/select';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { PaymentDefinitionsService } from '@services/payment-definitions.service';
import { IPspCurrencyType, ITypeListItem } from '@shared/interfaces/payment-definitions.interface';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-psp-currency',
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        DialogModule,
        ToastModule,
        FormsModule,
        ReactiveFormsModule,
        Select,
        ToggleSwitch,
        ColumnFilterComponent,
        IconField,
        InputIcon,
        PageHeaderComponent
    ],
    templateUrl: './psp-currency.component.html',
    styleUrl: './psp-currency.component.scss'
})
export class PspCurrencyComponent implements OnInit {
    @ViewChild('dt') dt!: Table;

    currencies: IPspCurrencyType[] = [];
    pspTypes: ITypeListItem[] = [];
    currencyCodes: string[] = [];
    loading = true;
    dialogVisible = false;
    isEditMode = false;
    selectedCurrency: IPspCurrencyType | null = null;
    submitted = false;
    currencyForm!: FormGroup;

    private paymentService = inject(PaymentDefinitionsService);
    private fb = inject(FormBuilder);
    private messageService = inject(MessageService);
    private cd = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.initializeForm();
        this.loadCurrencies();
        this.loadDropdowns();
    }

    onGlobalSearch(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.dt.filterGlobal(value, 'contains');
    }

    loadCurrencies(): void {
        this.loading = true;
        this.cd.detectChanges();
        this.paymentService.getCurrencies().subscribe({
            next: (res) => {
                this.currencies = res.pspCurrencyTypes || [];
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    loadDropdowns(): void {
        this.paymentService.getPspTypeInformation().subscribe({
            next: (res) => {
                this.pspTypes = res.typeList || [];
            }
        });
        this.paymentService.getCurrencyCodes().subscribe({
            next: (res) => {
                this.currencyCodes = res || [];
            }
        });
    }

    initializeForm(): void {
        this.currencyForm = this.fb.group({
            providerCode: ['', [Validators.required]],
            currencyCode: [null, [Validators.required]],
            pspTypeId: [null, [Validators.required]],
            active: [true]
        });
    }

    isInvalid(field: string): boolean {
        const control = this.currencyForm.get(field);
        return !!(control && control.invalid && (this.submitted || control.dirty || control.touched));
    }

    openCreate(): void {
        this.isEditMode = false;
        this.selectedCurrency = null;
        this.submitted = false;
        this.currencyForm.reset({ providerCode: '', currencyCode: null, pspTypeId: null, active: true });
        this.dialogVisible = true;
    }

    openEdit(currency: IPspCurrencyType): void {
        this.isEditMode = true;
        this.selectedCurrency = currency;
        this.submitted = false;
        this.currencyForm.patchValue({
            providerCode: currency.code,
            currencyCode: currency.code,
            pspTypeId: currency.pspTypeId,
            active: currency.isActive
        });
        this.dialogVisible = true;
    }

    cancelDialog(): void {
        this.dialogVisible = false;
    }

    saveCurrency(): void {
        this.submitted = true;
        if (this.currencyForm.invalid) {
            return;
        }

        const formData = this.currencyForm.value;

        if (!this.isEditMode) {
            this.paymentService
                .createCurrency({
                    providerCode: formData.providerCode,
                    currencyCode: formData.currencyCode,
                    pspTypeId: formData.pspTypeId
                })
                .subscribe({
                    next: () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Currency has been created successfully.'
                        });
                        this.loadCurrencies();
                        this.dialogVisible = false;
                    }
                });
        } else if (this.selectedCurrency) {
            this.paymentService
                .updateCurrency(this.selectedCurrency.id, {
                    providerCode: formData.providerCode,
                    currencyCode: formData.currencyCode,
                    pspTypeId: formData.pspTypeId,
                    active: formData.active
                })
                .subscribe({
                    next: () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Currency has been updated successfully.'
                        });
                        this.loadCurrencies();
                        this.dialogVisible = false;
                    }
                });
        }
    }
}
